import { useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useAppDispatch, useAppSelector } from '../../../app/store';
import { useLoginMutation } from '../api/authApi';
import { sessionRefreshed, logout as logoutAction } from '../slice';
import { storeToken, clearAuthStorage } from '../../../services/tokenService';
import type { LoginCredentials, OAuthTokenResponse } from '../types';
import { SPA_LOGOUT_ENDPOINT } from '../constants';

export const useAuth = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { user, isAuthenticated } = useAppSelector((state) => state.auth);

  const [loginMutation, { isLoading, error, reset }] = useLoginMutation();

  const login = useCallback(
    async (credentials: LoginCredentials) => {
      const res: OAuthTokenResponse = await loginMutation(credentials).unwrap();

      storeToken(res);

      dispatch(
        sessionRefreshed({
          accessToken: res.access_token,
          homePageUrl: res.home_page_url,
          logoPageUrl: res.logo_page_url,
        })
      );

      return res;
    },
    [loginMutation, dispatch]
  );

  const logout = useCallback(() => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
    void fetch(`${baseUrl}${SPA_LOGOUT_ENDPOINT}`, {
      method: 'POST',
      credentials: 'include',
    });
    clearAuthStorage();
    dispatch(logoutAction());
    navigate({ to: '/login' });
  }, [dispatch, navigate]);

  return {
    user,
    isAuthenticated,
    isLoading,
    error,
    resetError: reset,
    login,
    logout,
  };
};
